// Utility functions for formatting fields and values in a consistent way across components

// Format field name for better display in UI
export const formatFieldName = (field) => {
  switch (field) {
    case 'percentage_complete': return 'completion percentage';
    case 'point_of_contact': return 'point of contact';
    case 'start_month': return 'start month';
    case 'duration_months': return 'duration';
    case 'contract_added': return 'contract';
    case 'contract_deleted': return 'contract';
    case 'contract_updated': return 'contract';
    case 'payment_added': return 'payment';
    case 'payment_deleted': return 'payment';
    case 'payment_updated': return 'payment';
    default: return field;
  }
};

// Format currency values consistently
export const formatCurrency = (value) => {
  if (value === null || value === undefined) return 'N/A';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

// Format a contract object into a readable string - used by formatFieldValue
export const formatContractObject = (contract) => {
  if (!contract || typeof contract !== 'object') return 'N/A';
  
  const { description, amount, date, subcontractor, files } = contract;
  const formattedAmount = amount ? formatCurrency(amount) : 'N/A';
  const formattedDate = date ? new Date(date).toLocaleDateString() : '';
  const contractName = description || 'Contract';
  const attachments = files && files.length > 0 ? ` with ${files.length} attachment${files.length !== 1 ? 's' : ''}` : '';
  
  return { 
    name: contractName,
    fullText: `Contract: ${contractName}${formattedDate ? ` (${formattedDate})` : ''} - ${formattedAmount}${subcontractor ? ` with ${subcontractor}` : ''}${attachments}`
  };
};

// Format a payment object into a readable string - used by formatFieldValue
export const formatPaymentObject = (payment, scopeInfo = null) => {
  if (!payment || typeof payment !== 'object') return 'N/A';
  
  const { number, description, amount, date, files, payee } = payment;
  const formattedAmount = amount ? formatCurrency(amount) : 'N/A';
  const formattedDate = date ? new Date(date).toLocaleDateString() : '';
  const paymentName = number ? `#${number}` : (description || '');
  const attachments = files && files.length > 0 ? ` with ${files.length} attachment${files.length !== 1 ? 's' : ''}` : '';
  
  // Use payee if available, otherwise fallback to subcontractor from the scope if provided
  let payeeName = payee;
  if (!payeeName && scopeInfo && scopeInfo.subcontractor) {
    payeeName = scopeInfo.subcontractor;
  }
  const payeeInfo = payeeName ? ` to ${payeeName}` : '';
  
  return {
    name: paymentName ? `${paymentName}` : '',
    fullText: `Payment ${paymentName ? `${paymentName}` : ''}${formattedDate ? ` (${formattedDate})` : ''} - ${formattedAmount}${payeeInfo}${attachments}`
  };
};

// Format field values based on their type/field name
export const formatFieldValue = (field, value) => {
  if (value === null || value === undefined || value === '') return 'N/A';
  
  switch (field) {
    case 'budget':
      return formatCurrency(value);
    case 'percentage_complete':
      return `${value}%`;
    case 'status':
      return value;
    case 'notes':
      return value;
    case 'contract_added':
    case 'contract_updated':
    case 'contract_deleted': {
      const formatted = formatContractObject(value);
      return formatted === 'N/A' ? 'N/A' : formatted.fullText;
    }
    case 'payment_added':
    case 'payment_updated':
    case 'payment_deleted': {
      const formatted = formatPaymentObject(value);
      return formatted === 'N/A' ? 'N/A' : formatted.fullText;
    }
    default:
      return String(value);
  }
};

// Generate edit history message based on field, old value, and new value
export const generateEditMessage = (field, oldValue, newValue) => {
  // For contract operations
  if (field === 'contract_added') {
    const formatted = formatContractObject(newValue);
    if (formatted === 'N/A') return `added a contract`;
    return (
      <>
        added contract
        <div className="mt-2 mb-2 p-2 bg-gray-50 rounded-md text-gray-600 text-xs">
          <div>
            <span className="font-semibold">Details:</span>
            <div className="pl-2 border-l-2 border-indigo-300 mt-1 whitespace-pre-wrap">
              {formatted.fullText}
            </div>
          </div>
        </div>
      </>
    );
  }
  
  if (field === 'contract_deleted') {
    const formatted = formatContractObject(oldValue);
    if (formatted === 'N/A') return `removed a contract`;
    return (
      <>
        removed contract
        <div className="mt-2 mb-2 p-2 bg-gray-50 rounded-md text-gray-600 text-xs">
          <div>
            <span className="font-semibold">Details:</span>
            <div className="pl-2 border-l-2 border-gray-300 mt-1 whitespace-pre-wrap">
              {formatted.fullText}
            </div>
          </div>
        </div>
      </>
    );
  }
  
  if (field === 'contract_updated') {
    const oldFormatted = formatContractObject(oldValue);
    const newFormatted = formatContractObject(newValue);
    if (oldFormatted === 'N/A' || newFormatted === 'N/A') {
      return `updated contract`;
    }
    return (
      <>
        updated contract
        <div className="mt-2 mb-2 p-2 bg-gray-50 rounded-md text-gray-600 text-xs">
          <div className="mb-2">
            <span className="font-semibold">Previous:</span>
            <div className="pl-2 border-l-2 border-gray-300 mt-1 whitespace-pre-wrap">
              {oldFormatted.fullText}
            </div>
          </div>
          <div>
            <span className="font-semibold">Updated:</span>
            <div className="pl-2 border-l-2 border-indigo-300 mt-1 whitespace-pre-wrap">
              {newFormatted.fullText}
            </div>
          </div>
        </div>
      </>
    );
  }
  
  // For payment operations
  if (field === 'payment_added') {
    const formatted = formatPaymentObject(newValue);
    if (formatted === 'N/A') return `added a payment`;
    return (
      <>
        added payment
        <div className="mt-2 mb-2 p-2 bg-gray-50 rounded-md text-gray-600 text-xs">
          <div>
            <span className="font-semibold">Details:</span>
            <div className="pl-2 border-l-2 border-indigo-300 mt-1 whitespace-pre-wrap">
              {formatted.fullText}
            </div>
          </div>
        </div>
      </>
    );
  }
  
  if (field === 'payment_deleted') {
    const formatted = formatPaymentObject(oldValue);
    if (formatted === 'N/A') return `removed a payment`;
    return (
      <>
        removed payment
        <div className="mt-2 mb-2 p-2 bg-gray-50 rounded-md text-gray-600 text-xs">
          <div>
            <span className="font-semibold">Details:</span>
            <div className="pl-2 border-l-2 border-gray-300 mt-1 whitespace-pre-wrap">
              {formatted.fullText}
            </div>
          </div>
        </div>
      </>
    );
  }
  
  if (field === 'payment_updated') {
    const oldFormatted = formatPaymentObject(oldValue);
    const newFormatted = formatPaymentObject(newValue);
    if (oldFormatted === 'N/A' || newFormatted === 'N/A') {
      return `updated payment`;
    }
    return (
      <>
        updated payment
        <div className="mt-2 mb-2 p-2 bg-gray-50 rounded-md text-gray-600 text-xs">
          <div className="mb-2">
            <span className="font-semibold">Previous:</span>
            <div className="pl-2 border-l-2 border-gray-300 mt-1 whitespace-pre-wrap">
              {oldFormatted.fullText}
            </div>
          </div>
          <div>
            <span className="font-semibold">Updated:</span>
            <div className="pl-2 border-l-2 border-indigo-300 mt-1 whitespace-pre-wrap">
              {newFormatted.fullText}
            </div>
          </div>
        </div>
      </>
    );
  }
  
  // For other cases...
  // Empty old value = field was added
  if ((oldValue === null || oldValue === undefined || oldValue === '') && 
      (newValue !== null && newValue !== undefined && newValue !== '')) {
    return `added ${formatFieldName(field)} ${formatFieldValue(field, newValue)}`;
  }
  
  // Empty new value = field was removed
  if ((newValue === null || newValue === undefined || newValue === '') && 
      (oldValue !== null && oldValue !== undefined && oldValue !== '')) {
    return `removed ${formatFieldName(field)} ${formatFieldValue(field, oldValue)}`;
  }
  
  // Status needs special handling
  if (field === 'status') {
    return `changed status from ${oldValue} to ${newValue}`;
  }
  
  // Notes only needs simple message
  if (field === 'notes') {
    return `updated notes`;
  }
  
  // Default case - field was updated
  return `updated ${formatFieldName(field)} from ${formatFieldValue(field, oldValue)} to ${formatFieldValue(field, newValue)}`;
};